import { Component } from "@theme/component";
import { debounce, requestIdleCallback } from "@theme/utilities";
import { VideoOverlay } from "@theme/video-overlay";

export class FloatingVideoPlayerComponent extends Component {
  requiredRefs = ["dragHandle"];

  #isDragging = false;
  #startPosition = { x: 0, y: 0 };
  #currentPosition = { x: 0, y: 0 };
  #currentSize = { width: 300, height: 200 };
  #isInitialized = false;
  #resizeObserver = null;
  #controller = new AbortController();
  #padding = 20;
  #isMaximized = false;
  #originalPosition = { x: 0, y: 0 };
  #originalSize = { width: 300, height: 200 };
  #videoOverlay = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.#isInitialized) return;
    this.#isInitialized = true;

    // Wait for the element to be fully rendered before setting up
    requestIdleCallback(() => {
      this.#setupInitialPosition();
      this.#setupEventListeners();
      this.#setupResizeObserver();
      this.#setupCloseButton();
      this.#setupMaximizeButton();
      this.#setupVideoControls();
      this.#setupVideoOverlay();
    });
  }

  #setupInitialPosition() {
    const position = this.dataset.initialPosition || "bottom-right";
    const size = this.dataset.playerSize || "medium";

    // Set size first
    this.#setInitialSize(size);
    this.classList.add(`floating-video-player--${size}`);

    // Wait for the element to be properly sized and positioned
    setTimeout(() => {
      this.#setPositionFromPreset(position);
    }, 200); // Increased timeout to ensure proper sizing
  }

  #setInitialSize(size) {
    switch (size) {
      case "small":
        this.#currentSize = { width: 200, height: 133 };
        break;
      case "medium":
        this.#currentSize = { width: 300, height: 200 };
        break;
      case "large":
        this.#currentSize = { width: 400, height: 267 };
        break;
    }
    this.#updateSize();
  }

  #setPositionFromPreset(preset) {
    // Force a reflow to ensure the element is properly sized
    this.offsetHeight;

    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    let x, y;

    switch (preset) {
      case "bottom-right":
        x = viewport.width - this.#currentSize.width - this.#padding;
        y = viewport.height - this.#currentSize.height - this.#padding;
        break;
      case "bottom-left":
        x = this.#padding;
        y = viewport.height - this.#currentSize.height - this.#padding;
        break;
      case "top-right":
        x = viewport.width - this.#currentSize.width - this.#padding;
        y = this.#padding;
        break;
      case "top-left":
        x = this.#padding;
        y = this.#padding;
        break;
      case "center":
        x = (viewport.width - this.#currentSize.width) / 2;
        y = (viewport.height - this.#currentSize.height) / 2;
        break;
      default:
        // Default to bottom-right if preset is not recognized
        x = viewport.width - this.#currentSize.width - this.#padding;
        y = viewport.height - this.#currentSize.height - this.#padding;
        break;
    }

    this.#currentPosition = { x, y };
    this.#originalPosition = { x, y };
    this.#originalSize = { ...this.#currentSize };
    this.#updatePosition();
  }

  #setupEventListeners() {
    const { signal } = this.#controller;

    if (this.dataset.enableDragging === "true") {
      this.addEventListener("mousedown", this.#handleMouseDown, { signal });
      this.addEventListener("touchstart", this.#handleTouchStart, { signal, passive: false });
    }

    this.addEventListener("click", this.#handleClick, { signal });
  }

  #setupCloseButton() {
    const closeButton = this.querySelector(".floating-video-player__close");
    if (closeButton) {
      closeButton.addEventListener("click", (e) => {
        e.stopPropagation();
        this.style.display = "none";
      });
    }
  }

  #setupMaximizeButton() {
    const maximizeButton = this.querySelector(".floating-video-player__maximize");
    if (maximizeButton) {
      maximizeButton.addEventListener("click", (e) => {
        e.stopPropagation();
        this.#toggleMaximize();
      });
    }
  }

  // Add this method to your existing class, after the #setupVideoControls method:

  #setupVideoControls() {
    const iframe = this.querySelector("iframe[data-video-type]");
    if (iframe) {
      // Always disable YouTube hover effects and branding
      if (iframe.src.includes("youtube.com")) {
        const url = new URL(iframe.src);
        url.searchParams.set("controls", "0");
        url.searchParams.set("showinfo", "0");
        url.searchParams.set("rel", "0");
        url.searchParams.set("modestbranding", "1");
        url.searchParams.set("iv_load_policy", "3");
        url.searchParams.set("fs", "0");
        url.searchParams.set("cc_load_policy", "0");
        url.searchParams.set("disablekb", "1");
        url.searchParams.set("enablejsapi", "1");
        url.searchParams.set("origin", window.location.origin);
        iframe.src = url.toString();
      }
    }
  }

  #setupVideoOverlay() {
    const video = this.querySelector("video, iframe");
    if (video) {
      this.#videoOverlay = new VideoOverlay(video);
    }
  }

  #handleMouseDown = (event) => {
    const isDragHandle = event.target.closest('[ref="dragHandle"]');
    const isVideo = event.target.closest("video");
    const isIframe = event.target.closest("iframe");
    const isControl = event.target.closest(".floating-video-player__controls");
    const isHeader = event.target.closest(".floating-video-player__header");
    const isCloseButton = event.target.closest(".floating-video-player__close");
    const isMaximizeButton = event.target.closest(".floating-video-player__maximize");

    // Don't allow dragging when maximized or from buttons
    if (this.#isMaximized || !isHeader || isVideo || isIframe || isControl || isCloseButton || isMaximizeButton) return;

    event.preventDefault();
    event.stopPropagation(); // Prevent link navigation during drag
    this.#startDrag(event.clientX, event.clientY);
  };

  #handleTouchStart = (event) => {
    const isDragHandle = event.target.closest('[ref="dragHandle"]');
    const isVideo = event.target.closest("video");
    const isIframe = event.target.closest("iframe");
    const isControl = event.target.closest(".floating-video-player__controls");
    const isHeader = event.target.closest(".floating-video-player__header");
    const isCloseButton = event.target.closest(".floating-video-player__close");
    const isMaximizeButton = event.target.closest(".floating-video-player__maximize");

    // Don't allow dragging when maximized or from buttons
    if (this.#isMaximized || !isHeader || isVideo || isIframe || isControl || isCloseButton || isMaximizeButton) return;

    event.preventDefault();
    event.stopPropagation(); // Prevent link navigation during drag
    const touch = event.touches[0];
    this.#startDrag(touch.clientX, touch.clientY);
  };

  #startDrag(startX, startY) {
    this.#isDragging = true;
    this.#startPosition = { x: startX, y: startY };

    this.classList.add("floating-video-player--dragging");

    const handleMove = (event) => {
      if (!this.#isDragging) return;

      const clientX = event.type.includes("touch") ? event.touches[0].clientX : event.clientX;
      const clientY = event.type.includes("touch") ? event.touches[0].clientY : event.clientY;

      const deltaX = clientX - this.#startPosition.x;
      const deltaY = clientY - this.#startPosition.y;

      this.#currentPosition.x += deltaX;
      this.#currentPosition.y += deltaY;

      this.#updatePosition();
      this.#startPosition = { x: clientX, y: clientY };
    };

    const handleEnd = () => {
      this.#isDragging = false;
      this.classList.remove("floating-video-player--dragging");

      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleEnd);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchmove", handleMove, { passive: false });
    document.addEventListener("touchend", handleEnd);
  }

  #handleClick = (event) => {
    if (this.#isDragging) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  #updatePosition() {
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Constrain to viewport with padding
    this.#currentPosition.x = Math.max(this.#padding, Math.min(this.#currentPosition.x, viewport.width - this.#currentSize.width - this.#padding));
    this.#currentPosition.y = Math.max(this.#padding, Math.min(this.#currentPosition.y, viewport.height - this.#currentSize.height - this.#padding));

    this.style.transform = `translate(${this.#currentPosition.x}px, ${this.#currentPosition.y}px)`;
  }

  #updateSize() {
    this.style.width = `${this.#currentSize.width}px`;
    this.style.height = `${this.#currentSize.height}px`;
  }

  #setupResizeObserver() {
    this.#resizeObserver = new ResizeObserver(
      debounce(() => {
        this.#updatePosition();
      }, 100)
    );

    this.#resizeObserver.observe(this);
  }

  #toggleMaximize() {
    if (this.#isMaximized) {
      this.#minimize();
    } else {
      this.#maximize();
    }
  }

  #maximize() {
    // Store current position and size before maximizing
    this.#originalPosition = { ...this.#currentPosition };
    this.#originalSize = { ...this.#currentSize };

    this.#isMaximized = true;
    this.classList.add("floating-video-player--maximized");

    // Update button aria-label
    const maximizeButton = this.querySelector(".floating-video-player__maximize");
    if (maximizeButton) {
      maximizeButton.setAttribute("aria-label", "Restore video player");
    }

    // Disable dragging when maximized
    this.style.cursor = "default";
  }

  #minimize() {
    this.#isMaximized = false;
    this.classList.remove("floating-video-player--maximized");

    // Update button aria-label
    const maximizeButton = this.querySelector(".floating-video-player__maximize");
    if (maximizeButton) {
      maximizeButton.setAttribute("aria-label", "Maximize video player");
    }

    // Restore original position and size
    this.#currentPosition = { ...this.#originalPosition };
    this.#currentSize = { ...this.#originalSize };

    // Re-enable dragging
    this.style.cursor = "";

    // Update position and size
    this.#updatePosition();
    this.#updateSize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
    if (this.#videoOverlay) {
      this.#videoOverlay.destroy();
    }
  }
}

if (!customElements.get("floating-video-player")) {
  customElements.define("floating-video-player", FloatingVideoPlayerComponent);
}
